import type { LogoRailItem } from '@carouselgg/nexus/logo-rail'
import type { MyOrg } from '@carouselgg/auth/use-my-orgs'
import type { MyTeam } from '@carouselgg/auth/use-my-teams'

// Your teams and orgs as rail tiles, in three groups split by a divider: your
// teams, the orgs you run, and the orgs you're part of. A tile with a logo
// carries its initials as a badge, since teams share their club's logo and
// related orgs often share one too. The tile for the page
// you're on is marked active.

const GAME_SHORT: Record<string, string> = {
  'league of legends': 'LoL',
  valorant: 'VAL',
  'counter-strike 2': 'CS2',
  'counter-strike': 'CS2',
  'rocket league': 'RL',
  'overwatch 2': 'OW2',
  overwatch: 'OW2',
  'dota 2': 'Dota',
}

const ORG_TYPE: Record<string, string> = { organization: 'Club', league: 'League', university: 'University' }
const ROLE_ORDER: Record<string, number> = { owner: 0, admin: 1, manager: 2, coach: 3, member: 4 }
/** Roles that run an org rather than belong to it. */
const MANAGES = new Set(['owner', 'admin', 'manager'])

const SMALL_WORDS = new Set(['of', 'the', 'and', 'a', 'an', 'at', 'in', '&'])

/**
 * "Panda Express" → "PE", "Carousel Rising University Esports League" → "CRU",
 * "Bamboo" → "BA": the first letter of up to three words (little words
 * skipped), or a single word's first two.
 */
export function initials(name: string): string {
  const words = name.split(/\s+/).filter((w) => w && !SMALL_WORDS.has(w.toLowerCase()))
  if (words.length === 0) return ''
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return words.slice(0, 3).map((w) => w.charAt(0)).join('').toUpperCase()
}

export function gameShort(gameName: string): string {
  return GAME_SHORT[gameName.toLowerCase()] ?? gameName
}

function title(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function teamHref(t: MyTeam): string {
  return `/org/${t.orgSlug}/teams/${t.slug}`
}

function under(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`)
}

/**
 * Your teams, the orgs you run, and the orgs you're part of, as rail tiles.
 * `path` marks the one you're on.
 */
export function railGroups(teams: MyTeam[], orgs: MyOrg[], path: string): [LogoRailItem[], LogoRailItem[], LogoRailItem[]] {
  const teamTiles = [...teams]
    .sort((a, b) => a.gameName.localeCompare(b.gameName) || a.name.localeCompare(b.name))
    .map((t): LogoRailItem => ({
      id: `team-${t.id}`,
      label: t.name,
      description: ['Team', gameShort(t.gameName), title(t.role)].join(' · '),
      to: teamHref(t),
      // A team without its own logo wears its club's; with neither, it's drawn
      // in its brand colour (its own, else its club's) with its initials.
      logoUrl: t.logoUrl ?? t.orgLogoUrl,
      badge: (t.logoUrl ?? t.orgLogoUrl) ? initials(t.name) : undefined,
      color: t.primaryColor ?? t.orgPrimaryColor,
      initials: initials(t.name),
      active: under(path, teamHref(t)),
    }))
  // On a team's page the team is the space you're in, not its club.
  const onTeam = teamTiles.some((t) => t.active)
  const sorted = [...orgs]
    .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || a.name.localeCompare(b.name))
  // The rail goes to an org; managing it is the side nav's Manage section.
  const orgTile = (o: MyOrg): LogoRailItem => ({
    id: `org-${o.id}`,
    label: o.name,
    description: [ORG_TYPE[o.type] ?? 'Organization', title(o.role)].join(' · '),
    to: `/org/${o.slug}`,
    logoUrl: o.logoUrl,
    badge: o.logoUrl ? initials(o.name) : undefined,
    color: o.brandColor,
    initials: initials(o.name),
    active: !onTeam && under(path, `/org/${o.slug}`),
  })
  return [
    teamTiles,
    sorted.filter((o) => MANAGES.has(o.role)).map(orgTile),
    sorted.filter((o) => !MANAGES.has(o.role)).map(orgTile),
  ]
}

/** Org roles that open its manage dashboard: the full one, or (coach) the teams they coach. */
const MANAGE_ROLES = new Set(['owner', 'admin', 'manager', 'coach'])

/**
 * What you can manage, for the side nav's Manage section: every org you run
 * or coach in (its dashboard), then every team you captain in a club you
 * don't (that team's page in the dashboard). Same order as the rail.
 */
export function manageItems(teams: MyTeam[], orgs: MyOrg[], path: string): LogoRailItem[] {
  const managed = [...orgs]
    .filter((o) => MANAGE_ROLES.has(o.role))
    .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || a.name.localeCompare(b.name))
  const managedSlugs = new Set(managed.map((o) => o.slug))
  const orgItems = managed.map((o): LogoRailItem => ({
    id: `manage-org-${o.id}`,
    label: o.name,
    description: [ORG_TYPE[o.type] ?? 'Organization', title(o.role)].join(' · '),
    to: `/org/${o.slug}/manage`,
    logoUrl: o.logoUrl,
    color: o.brandColor,
    initials: initials(o.name),
    active: under(path, `/org/${o.slug}/manage`),
  }))
  const teamItems = [...teams]
    .filter((t) => t.role === 'captain' && !managedSlugs.has(t.orgSlug))
    .sort((a, b) => a.gameName.localeCompare(b.gameName) || a.name.localeCompare(b.name))
    .map((t): LogoRailItem => ({
      id: `manage-team-${t.id}`,
      label: t.name,
      description: ['Team', gameShort(t.gameName), 'Captain'].join(' · '),
      to: `/org/${t.orgSlug}/manage/teams/${t.id}`,
      logoUrl: t.logoUrl ?? t.orgLogoUrl,
      color: t.primaryColor ?? t.orgPrimaryColor,
      initials: initials(t.name),
      active: under(path, `/org/${t.orgSlug}/manage/teams/${t.id}`),
    }))
  return [...orgItems, ...teamItems]
}
